import {
  Users,
  LayoutGrid,
  Brain,
  BarChart3,
  ChevronLeft,
  LogOut,
  Settings,
  Mail,
  Compass,
} from "lucide-react";
import { Link, useLocation, Outlet, useNavigate } from "react-router";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";

const sidebarLinks = [
  { href: "/admin", label: "Dashboard", icon: BarChart3, exact: true },
  { href: "/admin/users", label: "Users", icon: Users },
  { href: "/admin/apps", label: "Apps", icon: LayoutGrid },
  { href: "/admin/waitlist", label: "Waitlist", icon: Mail },
  { href: "/admin/ai", label: "AI Analytics", icon: Brain },
  { divider: true, label: "Content" },
  {
    href: "/admin/explore-templates",
    label: "Explore Templates",
    icon: Compass,
  },
];

export function AdminLayout() {
  const { user, signOut } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (href: string, exact = false) => {
    if (exact) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const getInitials = (name?: string, email?: string) => {
    if (name) {
      return name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return email?.charAt(0).toUpperCase() || "U";
  };

  return (
    <div className="bg-muted/30 flex min-h-screen">
      {/* Sidebar */}
      <aside className="border-border bg-background fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r">
        {/* Logo */}
        <div className="border-border flex h-16 items-center gap-2 border-b px-6">
          <Link
            to="/"
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <div className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-lg">
              <span className="text-lg font-bold">T</span>
            </div>
            <span className="text-foreground text-xl font-semibold">Admin</span>
          </Link>
        </div>

        {/* Back to App */}
        <div className="px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="w-full justify-start"
          >
            <Link to="/">
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back to TinyApps
            </Link>
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {sidebarLinks.map(link =>
            "divider" in link ? (
              <div key={link.label} className="pt-4 pb-2">
                <div className="text-muted-foreground px-3 text-xs font-semibold tracking-wider uppercase">
                  {link.label}
                </div>
              </div>
            ) : (
              <Link
                key={link.href}
                to={link.href!}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive(link.href!, link.exact)
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <link.icon className="h-5 w-5" />
                {link.label}
              </Link>
            ),
          )}
        </nav>

        {/* User Info */}
        <div className="border-border border-t p-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage
                src={user?.avatarUrl || undefined}
                alt={user?.displayName || user?.email || "User"}
              />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(user?.displayName, user?.email)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-foreground truncate text-sm font-medium">
                {user?.displayName || "Admin"}
              </p>
              <p className="text-muted-foreground truncate text-xs">
                {user?.email}
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="ghost" size="sm" asChild className="flex-1">
              <Link to="/settings">
                <Settings className="mr-1 h-4 w-4" />
                Settings
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              className="text-destructive hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="min-w-0 flex-1 overflow-hidden pl-64">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
